import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { 
  CreditCard, 
  DollarSign, 
  CheckCircle, 
  XCircle,
  Clock,
  Search,
  Calendar,
  Mail,
  Phone,
  Package,
  CalendarCheck
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentManagement = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      const response = await axios.get(`${API}/payments`);
      setPayments(response.data);
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Erreur lors du chargement des paiements');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      completed: { label: 'Réussi', className: 'bg-green-100 text-green-800 border-green-200' },
      pending: { label: 'En attente', className: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
      initiated: { label: 'Initié', className: 'bg-blue-100 text-blue-800 border-blue-200' },
      failed: { label: 'Échoué', className: 'bg-red-100 text-red-800 border-red-200' },
      expired: { label: 'Expiré', className: 'bg-gray-100 text-gray-800 border-gray-200' },
      refunded: { label: 'Remboursé', className: 'bg-purple-100 text-purple-800 border-purple-200' }
    };
    const badge = badges[status] || badges.pending;
    return <Badge className={`${badge.className} border`}>{badge.label}</Badge>;
  };

  const getPaymentTypeLabel = (type) => {
    const types = {
      product: 'Achat produit',
      appointment_deposit: 'Acompte rendez-vous',
      repair_deposit: 'Acompte réparation',
      other: 'Autre'
    };
    return types[type] || type;
  };

  const getPaymentTypeIcon = (type) => {
    switch (type) {
      case 'product':
        return <Package className="w-4 h-4" />;
      case 'appointment_deposit':
      case 'repair_deposit':
        return <CalendarCheck className="w-4 h-4" />;
      default:
        return <DollarSign className="w-4 h-4" />;
    }
  };

  const filteredPayments = payments.filter(payment => 
    payment.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    payment.customer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    payment.id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    total: payments.length,
    completed: payments.filter(p => p.payment_status === 'completed').length,
    totalAmount: payments
      .filter(p => p.payment_status === 'completed')
      .reduce((sum, p) => sum + p.amount, 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Gestion des Paiements</h1>
          <p className="text-slate-600 mt-1">Suivi des transactions Stripe</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="glass">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Paiements</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">{stats.total}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Paiements Réussis</p>
                <p className="text-3xl font-bold text-green-600 mt-2">{stats.completed}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Montant Total</p>
                <p className="text-3xl font-bold text-purple-600 mt-2">
                  {stats.totalAmount.toFixed(2)}€
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="glass">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Rechercher par nom, email, ou ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Payments List */}
      <Card className="glass">
        <CardHeader>
          <CardTitle>Liste des Paiements</CardTitle>
          <CardDescription>
            {filteredPayments.length} paiement(s) trouvé(s)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredPayments.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Aucun paiement trouvé</p>
              </div>
            ) : (
              filteredPayments.map((payment) => (
                <div
                  key={payment.id}
                  className="border rounded-lg p-4 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                          {payment.customer_name?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{payment.customer_name}</h3>
                          <div className="flex items-center space-x-2 text-sm text-slate-600">
                            <Mail className="w-3 h-3" />
                            <span>{payment.customer_email}</span>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-3">
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Montant</p>
                          <p className="font-bold text-lg text-purple-600">
                            {payment.amount.toFixed(2)} {payment.currency.toUpperCase()}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs text-slate-500 mb-1">Type</p>
                          <div className="flex items-center space-x-2">
                            {getPaymentTypeIcon(payment.payment_type)}
                            <span className="text-sm">{getPaymentTypeLabel(payment.payment_type)}</span>
                          </div>
                        </div>

                        <div>
                          <p className="text-xs text-slate-500 mb-1">Date</p>
                          <div className="flex items-center space-x-2 text-sm">
                            <Calendar className="w-3 h-3 text-slate-400" />
                            <span>
                              {new Date(payment.created_at).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                        </div>

                        <div>
                          <p className="text-xs text-slate-500 mb-1">Statut</p>
                          {getStatusBadge(payment.payment_status)}
                        </div>
                      </div>

                      {payment.stripe_session_id && (
                        <div className="mt-3 text-xs text-slate-500 font-mono">
                          ID: {payment.stripe_session_id}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentManagement;
